import React, { useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import RenderImg from '@op/components/RenderImg';
import type { ImageViewerProps } from './types';
import SvgIcon from '@home/components/SvgIcon';
import { DefaultImg } from '@op/constants/defaultImg';

const ImageViewer: React.FC<ImageViewerProps> = ({
  images,
  currentIndex,
  onIndexChange,
  onClose,
}) => {
  // Preload adjacent images
  useEffect(() => {
    const preload = (idx: number) => {
      if (idx >= 0 && idx < images.length) {
        const img = new Image();
        img.src = images[idx];
      }
    };
    preload(currentIndex - 1);
    preload(currentIndex + 1);
  }, [currentIndex, images]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
        onIndexChange(currentIndex - 1);
      } else if (e.key === 'ArrowRight' && currentIndex < images.length - 1) {
        onIndexChange(currentIndex + 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, images.length, onClose, onIndexChange]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  const showPrev = currentIndex > 0;
  const showNext = currentIndex < images.length - 1;

  const content = (
    <div
      className="dbo-image-gallery__viewer"
      onClick={handleBackdropClick}
    >
      <button
        className="dbo-image-gallery__viewer-close"
        onClick={onClose}
        aria-label="Close"
      >
        <SvgIcon type={'clonse'} />
      </button>

      <div className="dbo-image-gallery__viewer-body" onClick={handleBackdropClick}>
        {showPrev && (
          <button
            className="dbo-image-gallery__viewer-arrow dbo-image-gallery__viewer-arrow--left"
            onClick={(e) => {
              e.stopPropagation();
              onIndexChange(currentIndex - 1);
            }}
            aria-label="Previous"
          >
            <SvgIcon type={'point-left'} />
          </button>
        )}

        <div className="dbo-image-gallery__viewer-image-wrap">
          <RenderImg
            coverImage={images[currentIndex]}
            mode="image"
            className="dbo-image-gallery__viewer-image"
            style={{
              maxWidth: '80vw',
              maxHeight: '70vh',
              objectFit: 'contain',
            }}
            defaultImage={DefaultImg.VIDEO}
          />
          {images.length > 1 && (
            <span className="dbo-image-gallery__viewer-counter">
              {currentIndex + 1} / {images.length}
            </span>
          )}
        </div>

        {showNext && (
          <button
            className="dbo-image-gallery__viewer-arrow dbo-image-gallery__viewer-arrow--right"
            onClick={(e) => {
              e.stopPropagation();
              onIndexChange(currentIndex + 1);
            }}
            aria-label="Next"
          >
            <SvgIcon type={'point-right'} />
          </button>
        )}
      </div>

      <div className="dbo-image-gallery__viewer-thumbs">
        {images.map((url, idx) => (
          <div
            key={`${url}-${idx}`}
            className={`dbo-image-gallery__viewer-thumb${idx === currentIndex ? ' dbo-image-gallery__viewer-thumb--active' : ''}`}
          >
            <RenderImg
              coverImage={url}
              mode="image"
              onClick={() => onIndexChange(idx)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              defaultImage={DefaultImg.VIDEO}
            />
          </div>
        ))}
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body) as unknown as React.ReactElement;
};

export default ImageViewer;
